import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as ExcelJS from 'exceljs';

const EXCEL_EXTENSION = '.xlsx';

@Injectable({ providedIn: 'root' })
export class ExcelPopulateService {
  constructor() {}

  public async exportAsExcelFile(json: any[], excelFileName: string, titulo: string, conEncabezado: boolean = true, columnasTexto: string[] = []): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('data');

    // 10. Insertar header de la tabla de datos en la fila siguiente
    let headerRowIndex = worksheet.rowCount + 1;

    if (conEncabezado) {
      // Leer datos de logo y colegio
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      const logoBase64 = authData?.logo?.logotipo64?.replace(/^data:image\/(png|jpg|jpeg);base64,/, '');
      const logoWidth = Number(authData?.logo?.width || 110);
      const logoHeight = Number(authData?.logo?.height || 130);
      const nombreColegio = authData?.logo?.nombre ?? '';

      // Dirección, teléfono y correo
      const datosColegio: string[] = [
        authData?.logo?.direccion || '',
        authData?.logo?.telefono || '',
        authData?.logo?.correo || ''
      ].filter((v: string) => v && v.trim() !== '');

      // 1. Definir cuántas filas ocuparán el logo y los datos del colegio
      const infoRows = Math.max(4, datosColegio.length + 1); // mínimo 4 filas (logo, nombre, datos)
      const rowTitulo = infoRows + 1; // El título irá justo después del bloque de info

      // 2. Aumentar la altura de las filas para el logo
      const logoRowStart = 1;
      const logoRowEnd = infoRows;
      for (let i = logoRowStart; i <= logoRowEnd; i++) {
        worksheet.getRow(i).height = logoHeight / infoRows;
      }

      // 3. Merge de las celdas de A1 a A[infoRows] para el logo
      worksheet.mergeCells(`A${logoRowStart}:A${logoRowEnd}`);

      // 4. Insertar el logo alineado a la derecha en ese bloque de filas
      if (logoBase64) {
        const imageId = workbook.addImage({
          base64: logoBase64,
          extension: 'png',
        });
        worksheet.addImage(imageId, {
          tl: { col: 0.5, row: 0 }, // más a la derecha de la columna A
          ext: { width: logoWidth, height: logoHeight }
        });
        worksheet.getColumn(1).width = Math.ceil(logoWidth / 7) + 2;
      }

      // 5. Insertar nombre de colegio grande en la columna B, fila 1
      worksheet.getCell('B1').value = nombreColegio;
      worksheet.getCell('B1').font = { size: 18, bold: true };
      worksheet.getCell('B1').alignment = { vertical: 'middle', horizontal: 'left' };
      worksheet.mergeCells(`B1:D1`);

      // 6. Insertar los datos (dirección, teléfono, correo) en B2, B3, ...
      datosColegio.forEach((value, idx) => {
        const rowIdx = idx + 2;
        worksheet.getCell(`B${rowIdx}`).value = value;
        worksheet.getCell(`B${rowIdx}`).font = { size: 12 };
        worksheet.getCell(`B${rowIdx}`).alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells(`B${rowIdx}:D${rowIdx}`);
      });

      // 7. Rellenar filas vacías hasta la [infoRows] (el header de datos estará después)
      while (worksheet.rowCount < infoRows) worksheet.addRow([]);

      // **8. Insertar el TÍTULO en la fila siguiente (Fila [infoRows + 1])**
      worksheet.getCell(`B${rowTitulo}`).value = titulo;
      worksheet.getCell(`B${rowTitulo}`).font = { size: 15, bold: true };
      worksheet.getCell(`B${rowTitulo}`).alignment = { vertical: 'middle', horizontal: 'left' };
      worksheet.mergeCells(`B${rowTitulo}:D${rowTitulo}`);

      // 9. Rellenar una fila vacía para separar el título de la tabla de datos (opcional)
      worksheet.addRow([]);

      headerRowIndex = worksheet.rowCount + 1;
    }
    if (json.length > 0) {
      const headers = Object.keys(json[0]);
      const headerRow = worksheet.addRow(headers);
      headerRow.font = { bold: true };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };
      headerRow.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' }, bottom: { style: 'thin' },
          left: { style: 'thin' }, right: { style: 'thin' }
        };
      });

      // Activar autofiltro en los headers
      worksheet.autoFilter = {
        from: { row: headerRowIndex, column: 1 },
        to: { row: headerRowIndex, column: headers.length }
      };

      // Aplicar formato texto a columnas que deben mostrarse como texto
      headers.forEach((header, idx) => {
        if (columnasTexto.includes(header)) {
          worksheet.getColumn(idx + 1).numFmt = '@';
        }
      });

      // Insertar la data debajo del header
      json.forEach(obj => {
        const rowData = headers.map(header => {
          const value = obj[header];
          if (columnasTexto.includes(header)) return value != null ? String(value) : '';
          if (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))) return Number(value);
          return value ?? '';
        });
        worksheet.addRow(rowData);
      });

      // Ajustar el ancho de las columnas de la tabla (desde headerRowIndex)
      headers.forEach((header, idx) => {
        const col = worksheet.getColumn(idx + 1);
        const relevantValues: (string | number)[] = [];
        for (let i = headerRowIndex; i <= worksheet.rowCount; i++) {
          let cellValue = worksheet.getCell(i, idx + 1).value;
          if (cellValue && typeof cellValue === 'object') {
            if ('result' in cellValue && (typeof (cellValue as any).result === 'string' || typeof (cellValue as any).result === 'number')) {
              cellValue = (cellValue as any).result;
            } else {
              cellValue = '';
            }
          }
          if (typeof cellValue === 'string' || typeof cellValue === 'number') {
            relevantValues.push(cellValue);
          }
        }
        const maxLen = Math.max(
          header.length,
          ...relevantValues.map(v => v?.toString().length ?? 0)
        );
        col.width = Math.max(12, maxLen + 2);
      });

      worksheet.views = [{ state: 'frozen', ySplit: headerRowIndex }]; // Freeze header
    }

    // 11. Guardar archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(blob, `${excelFileName}_export_${new Date().getTime()}.xlsx`);
  }

  /**
   * Genera un Excel con una hoja por cada unidad (incluye "Promedio General" cuando viene incluida
   * en `unidades`), replicando la misma data que usa el reporte en PDF (PDFService.getNotasUnidadPDF).
   */
  public async getNotasUnidadExcel(obj: { objGrado: any; logo: string; nombre: string; unidades: any[]; materias: any[]; notaMin: number }): Promise<void> {
    const { objGrado, logo, nombre, unidades, materias, notaMin } = obj;
    const workbook = new ExcelJS.Workbook();
    const logoBase64 = logo?.replace(/^data:image\/(png|jpg|jpeg);base64,/, '');
    const unidadesConNotas = (unidades || []).filter(u => (u.notas || []).length > 0);

    const usedNames = new Set<string>();
    const sanitizeSheetName = (name: string): string => {
      const clean = (name || 'Hoja').replace(/[\\\/\*\?\[\]:]/g, '-').substring(0, 31);
      let finalName = clean;
      let i = 1;
      while (usedNames.has(finalName)) {
        const suffix = ` (${i++})`;
        finalName = clean.substring(0, 31 - suffix.length) + suffix;
      }
      usedNames.add(finalName);
      return finalName;
    };

    for (const unidad of unidadesConNotas) {
      const worksheet = workbook.addWorksheet(sanitizeSheetName(unidad.nombre));

      // Encabezado: logo + datos del colegio/grado
      worksheet.mergeCells('A1:A4');
      if (logoBase64) {
        const imageId = workbook.addImage({ base64: logoBase64, extension: 'png' });
        worksheet.addImage(imageId, { tl: { col: 0.2, row: 0.1 }, ext: { width: 70, height: 70 } });
      }
      worksheet.getCell('B1').value = nombre || '';
      worksheet.getCell('B1').font = { size: 16, bold: true };
      worksheet.mergeCells('B1:D1');

      worksheet.getCell('B2').value = `Grado: ${objGrado?.gradoNombre || ''} ${objGrado?.seccionNombre || ''}`;
      worksheet.mergeCells('B2:D2');
      worksheet.getCell('B3').value = `Carrera: ${objGrado?.carreraNombre || ''}`;
      worksheet.mergeCells('B3:D3');
      worksheet.getCell('B4').value = `Ciclo: ${objGrado?.cicloNombre || ''}`;
      worksheet.mergeCells('B4:D4');

      worksheet.getCell('B6').value = unidad.nombre;
      worksheet.getCell('B6').font = { size: 14, bold: true };
      worksheet.mergeCells('B6:D6');

      const headerRowIndex = 8;
      const headers = ['Estudiante', ...materias.map(m => m.nombre), 'Promedio'];
      const headerRow = worksheet.getRow(headerRowIndex);
      headers.forEach((h, idx) => {
        const cell = headerRow.getCell(idx + 1);
        cell.value = h;
        cell.font = { bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };
        cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        cell.alignment = { horizontal: idx === 0 ? 'left' : 'center' };
      });

      (unidad.notas || []).forEach((notaObj: any, rowOffset: number) => {
        const row = worksheet.getRow(headerRowIndex + 1 + rowOffset);
        row.getCell(1).value = notaObj.estudiante;
        materias.forEach((m, idx) => {
          const value = notaObj[m.id];
          const cell = row.getCell(idx + 2);
          cell.value = value !== null && value !== undefined && value !== '' ? Number(value) : null;
          cell.alignment = { horizontal: 'center' };
          if (value !== null && value !== undefined && value !== '' && Number(value) < notaMin) {
            cell.font = { color: { argb: 'FFFF0000' } };
          }
        });
        const promedioCell = row.getCell(materias.length + 2);
        const promedio = notaObj.promedio !== null && notaObj.promedio !== undefined ? Number(notaObj.promedio) : null;
        promedioCell.value = promedio;
        promedioCell.alignment = { horizontal: 'center' };
        promedioCell.font = { bold: true, ...(promedio !== null && promedio < notaMin ? { color: { argb: 'FFFF0000' } } : {}) };
      });

      worksheet.autoFilter = { from: { row: headerRowIndex, column: 1 }, to: { row: headerRowIndex, column: headers.length } };
      worksheet.views = [{ state: 'frozen', ySplit: headerRowIndex }];

      worksheet.getColumn(1).width = 30;
      for (let c = 2; c <= headers.length; c++) {
        worksheet.getColumn(c).width = 14;
      }
    }

    if (workbook.worksheets.length === 0) {
      workbook.addWorksheet('Sin datos').getCell('A1').value = 'No hay notas registradas';
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(blob, `Notas_Unidad_${new Date().getTime()}.xlsx`);
  }
}
